import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../../components/snoop/SummaryDashboard'
import AssetsCreditsPanel from '../../components/snoop/AssetsCreditsPanel'
import {
  fetchAssets,
  fetchAssetsSongCredits,
  fetchCreative,
  setReleaseLock,
} from '../../api/operationsClient'
import {
  PLACEHOLDER_OPS_ASSETS,
  PLACEHOLDER_OPS_CREATIVE,
} from '../../utils/snoop/placeholderColumns'

const STATUS_OPTIONS = [
  { value: '', label: 'All releases' },
  { value: 'complete', label: 'Complete' },
  { value: 'missing', label: 'Missing something' },
]

const selectClass =
  'rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 shadow-sm focus:border-gray-400 focus:outline-none'

/**
 * Operations -> Assets / Creative.
 *
 * Both views render the same Box crawl snapshot (one row per release, tracks
 * nested under `_tracks`); they differ only in which facet columns show. The
 * crawl precomputes each facet label, so nothing here needs Box access.
 *
 * `view` is 'assets' (A&R facets) or 'creative'.
 */
export default function OpsAssetsPage({ view = 'assets' }) {
  const [status, setStatus] = useState('')
  // Credits fetched once per track and reused when a row is re-expanded.
  const [creditsCache, setCreditsCache] = useState({})
  const [lockBusy, setLockBusy] = useState({})
  const [lockState, setLockState] = useState({})
  const isCreative = view === 'creative'

  const fetchData = useCallback(() => {
    const opts = { status: status || undefined }
    return isCreative ? fetchCreative(opts) : fetchAssets(opts)
  }, [isCreative, status])

  // Newest folders first, matching upstream.
  const defaultSort = useMemo(() => [{ column: 'Added', direction: 'desc' }], [])

  const onCreditsLoaded = useCallback((trackFolderId, credits) => {
    setCreditsCache((prev) => ({ ...prev, [trackFolderId]: credits }))
  }, [])

  const renderNestedDetail = useCallback(
    (child) => {
      const tid = child?._release_folder_id
      return (
        <AssetsCreditsPanel
          trackFolderId={tid}
          initialCredits={creditsCache[tid]}
          fetchCredits={fetchAssetsSongCredits}
          onLoaded={onCreditsLoaded}
        />
      )
    },
    [creditsCache, onCreditsLoaded],
  )

  /**
   * Lock marks a release family done so the external crawl can skip it.
   * Optimistic: the row shows the new state immediately and reverts on failure.
   */
  const rowActions = useCallback(
    (row) => {
      const fid = row?._release_folder_id
      if (!fid) return null
      const locked = lockState[fid] ?? Boolean(row?._locked)
      const busy = Boolean(lockBusy[fid])
      return (
        <button
          type="button"
          disabled={busy}
          title={locked ? 'Unlock — let the crawl refresh this release' : 'Lock — mark this release done'}
          aria-pressed={locked}
          onClick={async () => {
            setLockBusy((p) => ({ ...p, [fid]: true }))
            const next = !locked
            setLockState((p) => ({ ...p, [fid]: next }))
            try {
              await setReleaseLock({ releaseFolderId: fid, locked: next })
            } catch {
              setLockState((p) => ({ ...p, [fid]: locked }))
            } finally {
              setLockBusy((p) => ({ ...p, [fid]: false }))
            }
          }}
          className={[
            'inline-flex h-7 w-7 items-center justify-center rounded-md transition',
            busy ? 'animate-pulse opacity-60' : '',
            locked
              ? 'text-amber-600 hover:bg-amber-50'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700',
          ].join(' ')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            {locked ? (
              <path
                d="M7 10V7a5 5 0 0 1 10 0v3M5 10h14v10H5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <path
                d="M7 10V7a5 5 0 0 1 9.6-2M5 10h14v10H5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </svg>
        </button>
      )
    },
    [lockState, lockBusy],
  )

  const extraActions = (
    <label className="inline-flex items-center gap-2 text-sm text-gray-600">
      Show
      <select
        className={selectClass}
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      >
        {STATUS_OPTIONS.map((opt) => (
          <option key={opt.value || 'all'} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  )

  return (
    <SummaryDashboard
      title={isCreative ? 'Creative' : 'Assets'}
      subtitle={
        isCreative
          ? 'Box inventory — cover art, canvas, banners, video, photos, physical'
          : 'Box inventory — agreements, credits, masters, stems and lyrics'
      }
      fetchData={fetchData}
      defaultSort={defaultSort}
      searchPlaceholder="Search artist, release or track…"
      extraActions={extraActions}
      layout="mapping"
      truncateColumns={['Artist', 'Release']}
      headerLabels={{ Bucket: 'Type', Agreements: 'Agmts', Credits: 'Cred' }}
      expandableNestedRows
      nestedRowsKey="_tracks"
      nestedExpandTitle="Show tracks"
      nestedRowClassName="bg-sky-50/90 text-slate-700"
      renderNestedDetail={renderNestedDetail}
      nestedDetailKey="_release_folder_id"
      nestedDetailTitle="Show credits"
      rowActions={rowActions}
      placeholderColumns={
        isCreative ? PLACEHOLDER_OPS_CREATIVE : PLACEHOLDER_OPS_ASSETS
      }
      tableKey={`ops-${view}-${status || 'all'}`}
    />
  )
}
